import { useMsal } from '@azure/msal-react';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/types';

interface InvestorId {
  investorId: string;
  username: string;
}

interface AuthContextType {
  investorId: InvestorId;
  setInvestorId: React.Dispatch<React.SetStateAction<InvestorId>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const msal = useMsal();
  const activeAccount = msal?.instance.getActiveAccount();
  const id = useSelector((store: RootState) => store.user.investorId); // Replace 'any' with actual store type if needed
  const [investorId, setInvestorId] = useState<InvestorId>({
    investorId: id || "",  // Default to an empty string if id is not available
    username: "",           // Set an empty username initially
  });

  useEffect(() => {
    // Only update state if activeAccount or id has changed
    if (activeAccount?.name !== investorId.username || id !== investorId.investorId) {
      setInvestorId({
        investorId: id,
        username: `${activeAccount?.name}` || "",  // Fallback to empty string if name is undefined
      });
    }
  }, [activeAccount, id]);  // Added id and investorId to dependencies to ensure proper updates

  return (
    <AuthContext.Provider value={{ investorId, setInvestorId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
